import "./compliance.css";
import "../../pages/businessAnalysis/businessAnalysis.css";
import { useRef, useState } from "react";
import dayjs from "dayjs";
import { DatePicker, Input, Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { finalizeDocumentUploadMutation, newDocumentVersionMutation, restoreComplianceDocumentMutation } from "@src/util/mutation/compliance.mutation";
import { describeAssignment, formatDate, RECURRENCE_LABEL, uploadThenFinalize } from "./complianceHelpers";

const passed = (value) => !!value && dayjs(value).isValid() && dayjs(value).isBefore(dayjs().startOf("day"));

/**
 * Restoring an archived compliance document. As it was - only when the server says it still can
 * (the current version, in date, the same file officers signed) - or with a new version, which is
 * the only way when it's out of date. A toolbox talk comes back as it was or as its next issue
 * (a fresh one everyone acknowledges). Either way a reason is asked for and kept in the audit trail.
 * `doc`: an Archived row (title, kind, version, archivedAt/By, reviewDate, expiryDate, restore { mode, reason }).
 */
export const RestoreDocumentModal = ({ doc, onClose, onRestored = () => {} }) => {
    const dispatch = useDispatch();
    const notify = (message, error = false) => dispatch(setNotification({ error, notificationState: true, message }));
    const allowed = doc?.restore?.mode || "no";
    const talk = doc?.kind === "toolboxTalk";
    const version = doc?.version || 1;
    const repeats = talk && doc?.recurrence && doc.recurrence !== "none" ? (RECURRENCE_LABEL[doc.recurrence] || doc.recurrence).toLowerCase() : null;
    const [how, setHow] = useState(allowed === "asIs" ? "asIs" : "newVersion");
    const [reason, setReason] = useState("");
    const [file, setFile] = useState(null);
    const reviewPassed = passed(doc?.reviewDate);
    const expiryPassed = passed(doc?.expiryDate);
    const [reviewDate, setReviewDate] = useState(reviewPassed ? "" : doc?.reviewDate || "");
    const [expiryDate, setExpiryDate] = useState(expiryPassed ? "" : doc?.expiryDate || "");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const [restore] = useMutation(restoreComplianceDocumentMutation);
    const [newDocumentVersion] = useMutation(newDocumentVersionMutation);
    const [finalizeUpload] = useMutation(finalizeDocumentUploadMutation);

    const title = doc?.title || "the document";
    const problem = allowed === "no" ? doc?.restore?.reason
        : reason.trim().length < 5 ? "Say why it's being restored."
        : talk ? null
        : how === "newVersion" && !file ? "Choose the new file."
        : how === "newVersion" && reviewPassed && !reviewDate ? "Set the next review date."
        : how === "newVersion" && passed(reviewDate) ? "The review date has to be in the future."
        : how === "newVersion" && passed(expiryDate) ? "The expiry date has to be in the future."
        : null;

    const submit = async () => {
        if (problem) return notify(problem, true);
        try {
            setBusy(true);
            if (how === "asIs") {
                const res = await restore({ variables: { documentId: doc._id, reason: reason.trim() } });
                const result = res?.data?.restoreComplianceDocument?.response;
                notify(`${title} restored - ${describeAssignment(result).toLowerCase()}. ${talk ? "Acknowledgements" : "Signatures"} already given still count.`);
            } else if (talk) {
                const res = await restore({ variables: { documentId: doc._id, reason: reason.trim(), asNewIssue: true } });
                const result = res?.data?.restoreComplianceDocument?.response;
                notify(`${title} restored as issue ${result?.version || version + 1} - ${describeAssignment(result).toLowerCase()} to acknowledge`);
            } else {
                const input = { fileName: file.name, restoreReason: reason.trim() };
                if (reviewDate !== (doc?.reviewDate || "")) input.reviewDate = reviewDate;
                if (expiryDate !== (doc?.expiryDate || "")) input.expiryDate = expiryDate;
                const res = await newDocumentVersion({ variables: { documentId: doc._id, input } });
                const { url, documentId, version } = res?.data?.newDocumentVersion?.response || {};
                const result = await uploadThenFinalize({ url, documentId, file, finalize: finalizeUpload });
                notify(`${title} restored as v${version} - ${describeAssignment(result).toLowerCase()} to sign`);
            }
            await onRestored();
            onClose();
        } catch (err) {
            notify(err.message, true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={!!doc}
            width={620}
            closable={false}
            onCancel={busy ? undefined : onClose}
            maskClosable={!busy}
            footer={
                <div className="drawer-footer" style={{ display: "flex", gap: 8 }}>
                    {allowed !== "no" && (
                        <button className={busy ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={busy} onClick={submit} data-restore-confirm>
                            {busy ? <Spinner /> : how === "asIs" ? "Restore" : talk ? "Restore as a new issue" : "Upload and restore"}
                        </button>
                    )}
                    <button className={busy ? "disabled-btn drawer-outlined-btn" : "drawer-outlined-btn"} disabled={busy} onClick={onClose}>
                        {allowed === "no" ? "Close" : "Cancel"}
                    </button>
                </div>
            }
        >
            <div className="modal-header-title">
                <span style={{ letterSpacing: "0.2px" }}>Restore {title}</span>
                <span onClick={() => !busy && onClose()}><FontAwesomeIcon className="close" icon={faClose} /></span>
            </div>

            <div className="modal-body" style={{ margin: "16px 0 0" }} data-restore-modal>
                <div className="ba-callout info">
                    <div>
                        <div className="ba-callout-title">{talk ? `Issue ${version}` : `v${version}`} · archived {formatDate(doc?.archivedAt)}{doc?.archivedBy ? ` by ${doc.archivedBy}` : ""}</div>
                        {talk ? "Acknowledgements" : "Signatures"} given before it was archived stay on record.
                    </div>
                </div>

                {allowed === "no" ? (
                    <div className="ba-callout warning" data-restore-blocked><div>{doc?.restore?.reason}</div></div>
                ) : (
                    <>
                        {allowed === "newVersion" && (
                            <div className="ba-callout warning" data-restore-why-new><div>{doc?.restore?.reason}</div></div>
                        )}

                        <div className="cmp-field">
                            <label>How it comes back</label>
                            <div className="cmp-choice">
                                {allowed === "asIs" && (talk ? (
                                    <button type="button" className={`cmp-choice-card${how === "asIs" ? " selected" : ""}`} onClick={() => setHow("asIs")} data-restore-as-is>
                                        <div className="cmp-choice-card-title">As it was (issue {version})</div>
                                        <div className="cmp-choice-card-text">The same issue officers acknowledged - its fingerprint is checked first. Acknowledgements already given still count; anyone else is asked.{repeats ? ` It repeats ${repeats} again from today.` : ""}</div>
                                    </button>
                                ) : (
                                    <button type="button" className={`cmp-choice-card${how === "asIs" ? " selected" : ""}`} onClick={() => setHow("asIs")} data-restore-as-is>
                                        <div className="cmp-choice-card-title">As it was (v{version})</div>
                                        <div className="cmp-choice-card-text">The same file officers signed - its fingerprint is checked first. Signatures already given still count; anyone else is asked to sign.</div>
                                    </button>
                                ))}
                                {talk ? (
                                    <button type="button" className={`cmp-choice-card${how === "newVersion" ? " selected" : ""}`} onClick={() => setHow("newVersion")} data-restore-new-version>
                                        <div className="cmp-choice-card-title">As a new issue (issue {version + 1})</div>
                                        <div className="cmp-choice-card-text">A fresh issue of the talk goes out and everyone it applies to acknowledges again.{repeats ? ` It repeats ${repeats} from today.` : ""}</div>
                                    </button>
                                ) : (
                                    <button type="button" className={`cmp-choice-card${how === "newVersion" ? " selected" : ""}`} onClick={() => setHow("newVersion")} data-restore-new-version>
                                        <div className="cmp-choice-card-title">With a new version</div>
                                        <div className="cmp-choice-card-text">Upload a reviewed file. Everyone it applies to is asked to sign the new version.</div>
                                    </button>
                                )}
                            </div>
                        </div>

                        {how === "newVersion" && !talk && (
                            <>
                                <div className="cmp-field">
                                    <label>New file</label>
                                    <div className="cmp-dropzone" onClick={() => fileRef.current?.click()} data-restore-file>
                                        {file ? <><div className="cmp-dropzone-file">{file.name}</div><div className="cmp-hint">Click to choose a different file</div></> : "Click to choose a PDF, Word document or image"}
                                    </div>
                                    <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                                </div>
                                <div style={{ display: "flex", gap: 16 }}>
                                    <div className="cmp-field" style={{ flex: 1 }}>
                                        <label>{reviewPassed ? "Next review date" : "Review by (optional)"}</label>
                                        <DatePicker className="generic-input-control" format="DD/MM/YYYY" value={reviewDate ? dayjs(reviewDate) : null}
                                            disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                                            onChange={(v) => setReviewDate(v ? v.format("YYYY-MM-DD") : "")} style={{ width: "100%" }} data-restore-review />
                                        {reviewPassed && <span className="cmp-hint">The last one ({formatDate(doc.reviewDate)}) has passed.</span>}
                                    </div>
                                    {doc?.expiryDate && (
                                        <div className="cmp-field" style={{ flex: 1 }}>
                                            <label>Expiry date</label>
                                            <DatePicker className="generic-input-control" format="DD/MM/YYYY" value={expiryDate ? dayjs(expiryDate) : null}
                                                disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                                                onChange={(v) => setExpiryDate(v ? v.format("YYYY-MM-DD") : "")} style={{ width: "100%" }} />
                                            {expiryPassed && <span className="cmp-hint">It expired on {formatDate(doc.expiryDate)}.</span>}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="cmp-field" style={{ marginBottom: 0 }}>
                            <label>Why is it being restored?</label>
                            <Input.TextArea rows={2} className="generic-input-control" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
                                placeholder="e.g. Back in use at the site after the refit" data-restore-reason />
                            <span className="cmp-hint">Kept in the audit trail with your name and the time.</span>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
